import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Node, ReactNodeViewRenderer, textblockTypeInputRule } from '@tiptap/react';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { MarkdownNodeSpec } from 'tiptap-markdown';

import { ExecutableCodeBlockNodeView } from './ExecutableCodeBlockNodeView';

const languageClassPrefix = 'language-';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    executableCodeBlock: {
      /**
       * Set a code block
       * @param attributes Code block attributes
       * @example editor.commands.setCodeBlock({ language: 'js' })
       */
      setCodeBlock: (attributes?: { language: string }) => ReturnType;
      /**
       * Toggle a code block
       * @param attributes Code block attributes
       * @example editor.commands.toggleCodeBlock({ language: 'js' })
       */
      toggleCodeBlock: (attributes?: { language: string }) => ReturnType;
      /**
       * Create a paragraph before the current block
       * @example editor.commands.insertParagraphBefore()
       */
      insertParagraphBefore: () => ReturnType;
    };
  }
}

/**
 * Matches a code block with backticks.
 */
export const backtickInputRegex = /^```([a-z]+)?[\s\n]$/;

/**
 * This extension allows you to create code blocks.
 * @see https://tiptap.dev/api/nodes/code-block
 */
export const ExecutableCodeBlockNode = Node.create({
  name: 'executableCodeBlock',

  content: 'text*',

  group: 'block',

  marks: '',

  code: true,

  defining: true,

  addStorage(): { markdown: MarkdownNodeSpec } {
    return {
      markdown: {
        serialize(state, node: ProseMirrorNode) {
          const code = node.textContent || '';

          state.write('```' + (node.attrs.language || ''));
          state.write('\n');
          state.text(code, false);
          state.ensureNewLine();
          state.write('```');
          state.closeBlock(node);
        },
        parse: {
          setup(markdownit) {
            markdownit.set({
              langPrefix: this.options.languageClassPrefix ?? 'language-',
            });
          },

          updateDOM(element) {
            element.innerHTML = element.innerHTML.replace(/\n<\/code><\/pre>/g, '</code></pre>');
          },
        },
      },
    };
  },

  addAttributes() {
    return {
      language: {
        default: null,
        parseHTML: element => {
          const classNames = [...(element.firstElementChild?.classList || [])];
          const languages = classNames
            .filter(className => className.startsWith(languageClassPrefix))
            .map(className => className.replace(languageClassPrefix, ''));
          const language = languages[0];

          if (!language) {
            return null;
          }

          return language;
        },
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'pre',
        preserveWhitespace: 'full',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'pre',
      HTMLAttributes,
      [
        'code',
        {
          class: node.attrs.language ? languageClassPrefix + node.attrs.language : null,
        },
        0,
      ],
    ];
  },

  addCommands() {
    return {
      setCodeBlock:
        attributes =>
        ({ commands }) => {
          return commands.setNode(this.name, attributes);
        },
      toggleCodeBlock:
        attributes =>
        ({ commands }) => {
          return commands.toggleNode(this.name, 'paragraph', attributes);
        },
      insertParagraphBefore:
        () =>
        ({ tr, dispatch, state: { schema } }) => {
          const { selection } = tr;
          const { $from } = selection;

          // Find the position before the current block
          const position = $from.before($from.depth);

          // Create a paragraph node
          const paragraph = schema.nodes.paragraph.create();

          // Insert the paragraph node at the identified position
          const transaction = tr.insert(position, paragraph);

          // Calculate the position of the newly inserted paragraph node
          const newPosition = position + 1;

          // Update the selection to the newly inserted paragraph node
          transaction.setSelection(TextSelection.create(tr.doc, newPosition));

          if (dispatch) {
            dispatch(transaction);
          }

          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // 'Mod-Alt-c': () => this.editor.commands.toggleCodeBlock(),
    };
  },

  addInputRules() {
    return [
      textblockTypeInputRule({
        find: backtickInputRegex,
        type: this.type,
        getAttributes: match => ({
          language: match[1],
        }),
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [
      // this plugin creates a code block for pasted content from VS Code
      // we can also detect the copied code language
      new Plugin({
        key: new PluginKey('codeBlockVSCodeHandler'),
        props: {
          handlePaste: (view, event) => {
            if (!event.clipboardData) {
              return false;
            }

            // don’t create a new code block within code blocks
            if (this.editor.isActive(this.type.name)) {
              return false;
            }

            const text = event.clipboardData.getData('text/plain');
            const vscode = event.clipboardData.getData('vscode-editor-data');
            const vscodeData = vscode ? JSON.parse(vscode) : undefined;
            const language = vscodeData?.mode;

            if (!text || !language) {
              return false;
            }

            const { tr, schema } = view.state;

            // prepare a text node
            // strip carriage return chars from text pasted as code
            // see: https://github.com/ProseMirror/prosemirror-view/commit/a50a6bcceb4ce52ac8fcc6162488d8875613aacd
            const textNode = schema.text(text.replace(/\r\n?/g, '\n'));

            // create a code block with the text node
            // replace selection with the code block
            tr.replaceSelectionWith(this.type.create({ language }, textNode));

            if (tr.selection.$from.parent.type !== this.type) {
              // put cursor inside the newly created code block
              tr.setSelection(
                TextSelection.near(tr.doc.resolve(Math.max(0, tr.selection.from - 2))),
              );
            }

            // store meta information
            // this is useful for other plugins that depends on the paste event
            // like the paste rule plugin
            tr.setMeta('paste', true);

            view.dispatch(tr);

            return true;
          },
        },
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ExecutableCodeBlockNodeView);
  },
});
